import React from "react";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { PiDotsThreeBold } from "react-icons/pi";
import ChevronLeftIcon from "SVGS/ChevronLeftIcon";
import ChevronRightIcon from "SVGS/ChevronRightIcon";

const DataTablePagination = ({
  pagination = { perPage: 10, pageNo: 1, totalCount: 0 },
  handlePerRowsChange,
  handlePageChange,
  loading = false,
  pageSizeOptions = [10, 20, 30, 40, 50, 100, 500, 1000]
}) => {
  const pageSize = pagination.perPage || 10;
  const currentPage = pagination.pageNo || 1;
  const totalCount = pagination.totalCount || 0;
  const totalPages = totalCount === 0 ? 1 : Math.ceil(totalCount / pageSize);

  const startIndex = totalCount === 0 ? 0 : ((currentPage - 1) * pageSize) + 1;
  const endIndex = Math.min(currentPage * pageSize, totalCount);

  const handlePageSizeChange = (e) => {
    const newPageSize = Number(e.target.value);
    if (handlePerRowsChange) {
      handlePerRowsChange(newPageSize, 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1 && !loading && handlePageChange) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages && !loading && handlePageChange) {
      handlePageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page) => {
    if (page !== currentPage && !loading && handlePageChange) {
      handlePageChange(page);
    }
  };

  const getVisiblePages = () => {
    const pages = [];
    const maxVisible = 5; // Show up to 5 page numbers

    if (totalPages <= maxVisible) {
      // Show all pages if total is less than maxVisible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate start and end of visible range around current page
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if we're near the beginning
      if (currentPage <= 3) {
        end = Math.min(4, totalPages - 1);
      }

      // Adjust if we're near the end
      if (currentPage >= totalPages - 2) {
        start = Math.max(totalPages - 3, 2);
      }

      // Add ellipsis and pages
      if (start > 2) {
        pages.push("ellipsis-start");
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages - 1) {
        pages.push("ellipsis-end");
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const visiblePages = getVisiblePages();

  return (
    <div className="flex items-center justify-between px-4 py-1 bg-white border-t border-gray-200">
      <div>
        <p className="text-sm text-gray-700">
          Showing{" "}
          <span className="font-medium">{startIndex}</span>
          {" "}-{" "}
          <span className="font-medium">{endIndex}</span>
          {" "}of{" "}
          <span className="font-medium">{totalCount}</span>
          {" "}results
        </p>
      </div>
      <div className="flex items-center space-x-4">
        <select
          value={pageSize}
          onChange={handlePageSizeChange}
          disabled={loading}
          className="px-2 py-1 text-sm border-gray-300 rounded-md outline-none focus:border-primaryColor focus:ring-primaryColor focus:ring-1"
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size} per page
            </option>
          ))}
        </select>
        <nav className="relative z-0 inline-flex -space-x-px">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 1 || loading}
            className={`relative inline-flex items-center px-2 py-2 text-sm font-medium ${
              currentPage === 1 || loading
                ? "text-opacity-50 text-gray-500 cursor-not-allowed"
                : "bg-white text-gray-500 hover:text-primaryColor "
            }`}
          >
            <LuChevronLeft size={18} />
          </button>
          {visiblePages.map((page, index) => {
            if (page === "ellipsis-start" || page === "ellipsis-end") {
              return (
                <span
                  key={`ellipsis-${index}`}
                  className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 text-opacity-50 bg-white"
                >
                  <PiDotsThreeBold size={20} />
                </span>
              );
            }
            return (
              <button
                key={page}
                onClick={() => handlePageClick(page)}
                disabled={loading}
                className={`relative inline-flex items-center px-3 py-2 text-sm font-medium ${
                  currentPage === page
                  ? "z-10 border border-primaryColor text-primaryColor  "
                    : "bg-white text-gray-500 hover:text-primaryColor "
                } ${loading ? "cursor-not-allowed" : ""}`}
              >
                {page}
              </button>
            );
          })}
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages || loading}
            className={`relative inline-flex items-center px-2 py-2 text-sm font-medium ${
              currentPage === totalPages || loading
              ? "text-opacity-50 text-gray-500 cursor-not-allowed"
                : "bg-white text-gray-500 hover:text-primaryColor "
            }`}
          >
            <LuChevronRight size={18} />
          </button>
        </nav>
      </div>
    </div>
  );
};

export default DataTablePagination;

